import { CONSTANTS } from '@toolproof-core/lib/_data/lookups';
import type {
    MutableTickTracePoint,
    MutableToolStep,
} from '@toolproof-core/lib/_contract/workflow-overlay';

import type { TraceSnapshotView } from '@/workflow-runner/_contract/traceSnapshot';

export function selectActivationTraceSnapshotView(args: {
    traceSnapshotView: TraceSnapshotView | undefined;
    targetTraceSnapshotView: TraceSnapshotView | undefined;
    pendingReplayEventKind: string | undefined;
}): TraceSnapshotView | undefined {
    return args.pendingReplayEventKind === 'primitive-operation'
        ? args.targetTraceSnapshotView ?? args.traceSnapshotView
        : args.traceSnapshotView;
}

/**
 * Derives the currently active tool step from a trace snapshot at the snapshot's
 * applied replay position index. Pure: no side effects or external state access.
 */
export function selectActiveToolStepFromTraceSnapshot(
    traceSnapshot: TraceSnapshotView | undefined
): MutableToolStep | undefined {
    const appliedReplayPositionIndex = traceSnapshot?.position.appliedReplayPositionIndex;

    if (
        traceSnapshot === undefined
        || appliedReplayPositionIndex === undefined
        || appliedReplayPositionIndex <= 0
    ) {
        return undefined;
    }

    const toolExecutionTicks = traceSnapshot.tickTracePoints.filter(
        (tracePoint): tracePoint is MutableTickTracePoint =>
            tracePoint.tracePointKind === 'tick'
            && tracePoint.tickKind === CONSTANTS.Enums.TickKind.tool_execution
            && tracePoint.stepKind === CONSTANTS.Enums.StepKind.tool
    );

    const activeTick = toolExecutionTicks[appliedReplayPositionIndex - 1];
    const runtimeStepKey = activeTick?.stepKey as MutableToolStep['stepKey'] | undefined;
    if (!runtimeStepKey) return undefined;

    const activeStepKey = traceSnapshot.macroStepControl.branch.replay.runtimeToBranchToolStepKey.get(runtimeStepKey) ?? runtimeStepKey;

    return traceSnapshot.position.orderedToolSteps.find(
        (toolStep) => toolStep.stepKey === activeStepKey
    );
}
